/**
 * @fileoverview Changing the picture at the top of Today.
 *
 * Today opens under the season's painting. A gardener can put a photograph
 * of their own garden there instead, and go back to the painting whenever
 * they like: choose a photo (on a phone the picker offers the camera too),
 * see it, save it. The photo is shrunk and turned upright on the phone
 * first, as a plant's or a bed's photos are, so an ordinary camera picture is
 * not refused for its size. An iPhone photo (HEIC) is made a JPEG first, the
 * server being unable to read one; one that will not convert is said, not
 * shown or saved.
 *
 * Where: opened from TodayPictureSetting on the profile (ProfilePreferences),
 * also with a late Google Photos pick already chosen (initialPhoto, B40).
 * API: PUT /today/photo (putTodayPhoto, multipart); DELETE /today/photo
 * (deleteTodayPhoto, back to the painting).
 * The photo is chosen with the same three ways as a plant's (PhotoSourcePicker:
 * camera, this device, Google Photos, useGooglePhotos / VITE_GOOGLE_CLIENT_ID),
 * in a screen of its own opened by the choose button.
 * Words (en/es): today.photo.title, .choose, .preparing, .save,
 * .useSeason, .failed, sitewide.cancel, sitewide.saving (Button loading).
 * A save that fails says why (lib/errorMessage): the server's refusal, the
 * standard sentence for a failure on our side (reported to Sentry, D31,
 * the support address a link), else today.photo.failed.
 *
 * @module TodayPhotoDialog
 */

import React, { useEffect, useRef, useState } from 'react';
import { Camera } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import { deleteTodayPhoto, putTodayPhoto } from '@/shared/api/today';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { PhotoDialogShell } from '@/features/plant-list/components/dialogs/photo/PhotoDialogShell';
import { PhotoSourcePicker } from '@/features/plant-list/components/dialogs/photo/PhotoSourcePicker';
import { useCamera } from '@/features/plant-list/components/dialogs/photo/useCamera';
import { useGooglePhotos } from '@/features/plant-list/components/dialogs/photo/useGooglePhotos';
import { PAINTING_ASPECT } from '../lib/paintings';

/** The dialog's state from the page, and what it tells the page. */
interface TodayPhotoDialogProps {
  /** Whether it is showing; each opening starts fresh. */
  isOpen: boolean;
  /** Closes it (not while saving). */
  onClose: () => void;
  /** What shows now: the garden's photo or the painting. */
  current: string | null;
  /** Where the page frames what shows now, so the preview crops it the same way. */
  currentPosition: string;
  /** True when the garden has a photo of its own, so the painting can be chosen again. */
  hasOwnPhoto: boolean;
  /** After a save or a return to the painting, so the page can read the day again. */
  onChanged: () => void;
  /** A photo already chosen (a Google Photos pick that came late, B40): previewed on opening, saved on Save. */
  initialPhoto?: File | null;
}

/**
 * Choose, preview and save a photo, or go back to the season's painting.
 *
 * @param props - TodayPhotoDialogProps.
 */
export const TodayPhotoDialog: React.FC<TodayPhotoDialogProps> = ({
  isOpen, onClose, current, currentPosition, hasOwnPhoto, onChanged, initialPhoto = null,
}) => {
  const { reactTranslate: t } = useTranslation();
  /** Converts an iPhone HEIC and shrinks and turns the chosen photo. */
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preparing' | 'saving' | null>(null);
  /** Why the last save failed (lib/errorMessage's sentence), or null. */
  const [failed, setFailed] = useState<string | null>(null);
  /** Why the photo chosen cannot be used (an iPhone photo that would not convert), or null. */
  const [unusable, setUnusable] = useState<string | null>(null);
  /** True while the photo source screen (camera, device, Google Photos) is open. */
  const [choosingPhoto, setChoosingPhoto] = useState(false);
  /** True once "Use the season's picture" was tapped, so that button (not Save) shows the loading look while the server works. */
  const [returning, setReturning] = useState(false);
  const camera = useCamera();
  const google = useGooglePhotos(choosingPhoto, { kind: 'today' });
  // The camera is released whenever the source screen closes.
  const { stop: stopCamera } = camera;
  useEffect(() => {
    if (!choosingPhoto) stopCamera();
  }, [choosingPhoto, stopCamera]);

  /**
   * Counts choices and openings, so a photo still being prepared when the
   * dialog closed (or another was chosen) is dropped when it is ready.
   */
  const attempt = useRef(0);

  // A fresh start each time it opens.
  useEffect(() => {
    if (!isOpen) return;
    attempt.current += 1;
    // A photo being prepared when it closed is dropped (above), so is its spinner.
    setBusy((was) => (was === 'preparing' ? null : was));
    setPhoto(null);
    setPreview(null);
    setFailed(null);
    setUnusable(null);
    setChoosingPhoto(false);
    setReturning(false);
  }, [isOpen]);

  // The browser holds each preview until it is released.
  useEffect(() => () => {
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
  }, [preview]);

  /**
   * Shrinks and turns the chosen photo (converting an iPhone HEIC first). A
   * photo that could not be made ready is not kept (preparePhoto, the one rule
   * for every picture) and the reason is said.
   *
   * @param file - The photo chosen, or null when the picker was closed empty.
   */
  const choose = async (file: File | null): Promise<void> => {
    if (!file) return;
    attempt.current += 1;
    const mine = attempt.current;
    setBusy('preparing');
    setFailed(null);
    setUnusable(null);
    const prepared = await preparePhoto(file);
    // Closed and opened again, or another photo chosen, meanwhile.
    if (mine !== attempt.current) return;
    if (!prepared.ok) {
      setUnusable(prepared.message);
      setBusy(null);
      return;
    }
    setPhoto(prepared.file);
    setPreview(URL.createObjectURL(prepared.file));
    setBusy(null);
  };

  /**
   * Runs a change, closing on success and saying so on failure.
   *
   * @param work - The save, or the return to the painting.
   */
  const change = async (work: () => Promise<unknown>): Promise<void> => {
    setBusy('saving');
    setFailed(null);
    setUnusable(null);
    try {
      await work();
      onChanged();
      onClose();
    } catch (error) {
      setFailed(errorMessage(error, t('today.photo.failed')));
    } finally {
      setBusy(null);
      setReturning(false);
    }
  };

  // A photo chosen before the dialog opened is taken as if it had just been chosen: shrunk and previewed, saved on Save.
  const chooseRef = useRef(choose);
  chooseRef.current = choose;
  useEffect(() => {
    if (isOpen && initialPhoto) void chooseRef.current(initialPhoto);
  }, [isOpen, initialPhoto]);

  const shown = preview ?? current;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && busy !== 'saving') onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('today.photo.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {shown && (
            <img
              src={shown}
              alt=""
              className="w-full rounded-md border object-cover"
              style={{ aspectRatio: PAINTING_ASPECT, objectPosition: preview ? 'center center' : currentPosition }}
            />
          )}
          {/* A flex column, so the shared Button fills the width without a size of its own. */}
          <div className="flex flex-col">
            <Button type="button" variant="outline" size="lg" disabled={busy !== null} onClick={() => setChoosingPhoto(true)}>
              <Camera className="mr-2 h-4 w-4" aria-hidden="true" />
              {busy === 'preparing' ? t('today.photo.preparing') : t('today.photo.choose')}
            </Button>
          </div>
          {unusable && <p className="text-sm text-destructive" role="alert">{unusable}</p>}
          {failed && <p className="text-sm text-destructive" role="alert"><FailureText text={failed} /></p>}
          <ActionRow
            onCancel={onClose}
            disabled={busy !== null}
            onRemove={hasOwnPhoto ? () => { setReturning(true); void change(deleteTodayPhoto); } : undefined}
            removeLabel={t('today.photo.useSeason')}
            removeLoading={busy === 'saving' && returning}
          >
            <Button
              type="button"
              size="lg"
              loading={busy === 'saving' && !returning}
              disabled={!photo || busy !== null}
              onClick={() => { if (photo) void change(() => putTodayPhoto(photo)); }}
            >
              {t('today.photo.save')}
            </Button>
          </ActionRow>
        </div>
      </DialogContent>
      <PhotoDialogShell isOpen={choosingPhoto} onClose={() => setChoosingPhoto(false)} title={t('today.photo.title')}>
        <PhotoSourcePicker
          camera={camera}
          onCaptured={(file) => { setChoosingPhoto(false); void choose(file); }}
          onFileChosen={(file) => { setChoosingPhoto(false); void choose(file); }}
          onPickGoogle={google.pick}
          isPickingGoogle={google.isPicking}
          googleStatus={google.status}
          googleCount={google.count}
          onCancelGoogle={google.cancel}
          googleError={google.error}
        />
      </PhotoDialogShell>
    </Dialog>
  );
};
